import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import type {
  BackupScheduleCreateRequestData,
  BackupScheduleData,
  BackupScheduleListItemData,
  BackupScheduleListQueryData,
  BackupScheduleUpdateRequestData,
} from "./types.js";
import { Project } from "../../project/index.js";

export class BackupSchedule extends ReferenceModel {
  public static ofId(id: string) {
    return new BackupSchedule(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.backupSchedule.find(id);
    if (data !== undefined) {
      return new BackupScheduleDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const schedule = await this.find(id);
    assertObjectFound(schedule, "backupSchedule", id);
    return schedule;
  });

  public findDetailed = provideReact(
    () => BackupSchedule.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<BackupScheduleDetailed | undefined>>;

  public getDetailed = provideReact(
    () => BackupSchedule.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<BackupScheduleDetailed>>;

  public static query(
    project: Project,
    query: BackupScheduleListQueryData = {},
  ) {
    return new BackupScheduleListQuery(project, query);
  }

  public static async create(
    project: Project,
    data: BackupScheduleCreateRequestData,
  ) {
    const response = await config.behaviors.backupSchedule.create(
      project.id,
      data,
    );

    if ("id" in response) {
      return new BackupSchedule(response.id);
    }

    return response;
  }

  public async delete() {
    await config.behaviors.backupSchedule.delete(this.id);
  }

  public async update(data: BackupScheduleUpdateRequestData) {
    return await config.behaviors.backupSchedule.update(this.id, data);
  }
}

class BackupScheduleCommon extends classes(
  DataModel<BackupScheduleListItemData | BackupScheduleData>,
  BackupSchedule,
) {
  public readonly description?: string;
  public readonly ttl?: string;
  public readonly schedule?: string;
  public readonly isSystemBackup: boolean;
  public readonly project: Project;

  public constructor(data: BackupScheduleListItemData | BackupScheduleData) {
    super([data], [data.id]);
    this.description = data.description;
    this.ttl = data.ttl;
    this.schedule = data.schedule;
    this.isSystemBackup = data.isSystemBackup;
    this.project = Project.ofId(data.projectId);
  }
}

export class BackupScheduleDetailed extends classes(
  BackupScheduleCommon,
  DataModel<BackupScheduleData>,
) {
  public constructor(data: BackupScheduleData) {
    super([data], [data]);
  }
}

export class BackupScheduleListItem extends classes(
  BackupScheduleCommon,
  DataModel<BackupScheduleListItemData>,
) {
  public constructor(data: BackupScheduleListItemData) {
    super([data], [data]);
  }
}

export class BackupScheduleListQuery extends ListQueryModel<BackupScheduleListQueryData> {
  public readonly project: Project;

  public constructor(
    project: Project,
    query: BackupScheduleListQueryData = {},
  ) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: BackupScheduleListQueryData) {
    return new BackupScheduleListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.backupSchedule.list(
      this.project.id,
      this.query,
    );
    return new BackupScheduleList(
      this.project,
      this.query,
      items.map((d) => new BackupScheduleListItem(d)),
    );
  }, [this.queryId, "backupSchedule"]);
}

export class BackupScheduleList extends classes(
  BackupScheduleListQuery,
  ListDataModel<BackupScheduleListItem>,
) {
  public constructor(
    project: Project,
    query: BackupScheduleListQueryData,
    schedules: BackupScheduleListItem[],
  ) {
    super([project, query], [schedules, schedules.length]);
  }
}
